/**
 * MCP server loader — discovers server configs (standard mcp.json locations,
 * hoocode's per-server JSON files, plugin registrations), connects via JSON-RPC
 * 2.0 over stdio (`command`), Streamable HTTP (`{ "type": "http", "url": ... }`),
 * or legacy SSE (`"type": "sse"`), and registers each server tool as
 * `mcp_<server>_<tool>`.
 *
 * Config sources (first-wins by server name):
 *   1. ~/.agents/mcp.json (user), ./.agents/mcp.json (project),
 *      ~/.config/claude/mcp.json (Claude Desktop)
 *   2. ~/.hoocode/mcp-servers/*.json and ./.hoocode/mcp-servers/*.json
 *   3. MCP servers registered by plugins/extensions during load
 */

import { type ChildProcess, spawn } from "node:child_process";
import { existsSync, readFileSync } from "node:fs";
import { readdir } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";
import { createInterface } from "node:readline";
import type { AgentToolResult, AgentToolUpdateCallback, McpRemoteOptions } from "@kolisachint/hoocode-agent-core";
import { connectHttpMcpServer, summarizeArgs } from "@kolisachint/hoocode-agent-core";
import { Text } from "@kolisachint/hoocode-tui";
import { type Static, Type } from "typebox";
import { getHooCodeDir } from "../../config.js";
import { ensureDenseIndex } from "../../core/capabilities/dense.js";
import { getCapabilities, registerCapabilities } from "../../core/capabilities/registry.js";
import { resetCapabilitySearch, searchCapabilities } from "../../core/capabilities/search.js";
import { getExtensionMcpServers } from "../../core/extension-mcp-servers.js";
import type { ExtensionAPI, ExtensionContext, SessionStartEvent, ToolDefinition } from "../../core/extensions/types.js";
import { formatDurationSecs } from "../../core/format-duration.js";
import { clearMcpServerStatuses, setMcpServerStatus } from "../../core/mcp-status.js";
import { deferMcpSchemas, subagentSkipMcp } from "../../core/subagent-depth.js";
import { taskStore } from "../../core/task-store.js";
import { shouldUseWindowsShell } from "../../utils/child-process.js";
import {
	type DeferredMcpToolEntry,
	formatDeferredCatalog,
	selectResolvable,
	toCapabilityDocs,
} from "./mcp-deferred.js";

const HOOCODE_DIR = getHooCodeDir();

interface McpToolDetails {
	elapsed?: number;
}

interface McpToolDef {
	name: string;
	description: string;
	inputSchema?: {
		type?: string;
		properties?: Record<string, { type?: string; description?: string }>;
		required?: string[];
	};
}

interface McpServerConfig {
	/** Unique server identifier used as prefix for registered tool names */
	name: string;
	/** Executable to spawn (stdio transport). One of command/url is required. */
	command?: string;
	/** Optional arguments passed to the command */
	args?: string[];
	/** Optional extra environment variables for the server process */
	env?: Record<string, string>;
	/** Transport: "stdio" (default with command), "http" (Streamable HTTP), or "sse" (legacy) */
	type?: "stdio" | "http" | "sse";
	/** Remote server URL (http/sse transports) */
	url?: string;
	/** Extra HTTP headers (e.g. Authorization) for remote transports */
	headers?: Record<string, string>;
	/** Run MCP tools in background by default (default: true for MCP servers) */
	background?: boolean;
}

/** Standard MCP config format used by Claude Desktop, Claude Code, VS Code / Copilot */
interface StandardMcpServerConfig {
	command?: string;
	args?: string[];
	env?: Record<string, string>;
	type?: "stdio" | "http" | "sse";
	url?: string;
	headers?: Record<string, string>;
	background?: boolean;
}

interface StandardMcpConfig {
	mcpServers?: Record<string, StandardMcpServerConfig>;
}

interface McpConnection {
	rpc(method: string, params?: unknown, timeoutMs?: number): Promise<unknown>;
	/** Send a JSON-RPC notification (no id, no response expected). */
	notify(method: string, params?: unknown): void;
	terminate(): void;
}

const mcpConnections = new Map<string, McpConnection>();
/**
 * Server configs retained by name so a tool call can transparently reconnect a
 * dropped server (process churn between turns, server exit, a racing teardown)
 * instead of permanently failing with "not connected".
 */
const mcpServerConfigs = new Map<string, McpServerConfig>();
/** Remote-transport options (OAuth storage, auth callbacks) per server name. */
const mcpRemoteOptions = new Map<string, McpRemoteOptions>();
/**
 * Servers with a browser authorization flow in flight. While a name is here,
 * a failed handshake must NOT tear the connection down (that would kill the
 * OAuth loopback listener); the auth-completion handler reconnects instead.
 */
const mcpAuthPending = new Set<string>();

/** Timeout for the connection handshake (initialize / tools/list). Tool calls
 *  themselves are left untimed since MCP tools can be long-running. */
const MCP_HANDSHAKE_TIMEOUT_MS = 15000;

function spawnMcpServer(config: McpServerConfig & { command: string }): McpConnection {
	const proc: ChildProcess = spawn(config.command, config.args ?? [], {
		env: { ...process.env, ...(config.env ?? {}) },
		stdio: ["pipe", "pipe", "pipe"],
		// `npx`/`npm` are .cmd shims on Windows, which spawn() cannot execute
		// directly — without this the launch fails with ENOENT.
		shell: shouldUseWindowsShell(config.command),
	});

	let nextId = 1;
	const pending = new Map<number, { resolve: (r: unknown) => void; reject: (e: Error) => void }>();
	/** Set once the child fails to launch, so later calls fail fast with the cause. */
	let launchError: Error | undefined;

	const rl = createInterface({ input: proc.stdout! });
	rl.on("line", (line) => {
		if (!line.trim()) return;
		try {
			const msg = JSON.parse(line) as {
				id?: number;
				result?: unknown;
				error?: { message: string };
			};
			if (msg.id === undefined) return;
			const cb = pending.get(msg.id);
			if (!cb) return;
			pending.delete(msg.id);
			if (msg.error) cb.reject(new Error(msg.error.message));
			else cb.resolve(msg.result);
		} catch {
			// ignore non-JSON server startup output
		}
	});

	proc.on("exit", () => {
		for (const cb of pending.values()) cb.reject(new Error(`MCP server "${config.name}" exited unexpectedly`));
		pending.clear();
		mcpConnections.delete(config.name);
	});

	// A ChildProcess that emits "error" with no listener takes the whole process
	// down with it. A missing MCP command is a bad config line, not a reason for
	// hoocode to refuse to start, so absorb it and let the handshake report it.
	proc.on("error", (err: NodeJS.ErrnoException) => {
		launchError =
			err.code === "ENOENT"
				? new Error(`MCP server "${config.name}": command not found: ${config.command}`)
				: new Error(`MCP server "${config.name}" failed to start: ${err.message}`);
		for (const cb of pending.values()) cb.reject(launchError);
		pending.clear();
		mcpConnections.delete(config.name);
	});
	// stdin dies with the child; its EPIPE surfaces through the same path.
	proc.stdin?.on("error", () => {});

	function rpc(method: string, params?: unknown, timeoutMs?: number): Promise<unknown> {
		if (launchError) return Promise.reject(launchError);
		const id = nextId++;
		return new Promise<unknown>((resolve, reject) => {
			let timer: NodeJS.Timeout | undefined;
			if (timeoutMs && timeoutMs > 0) {
				timer = setTimeout(() => {
					if (pending.delete(id)) {
						reject(new Error(`MCP server "${config.name}" timed out after ${timeoutMs}ms on ${method}`));
					}
				}, timeoutMs);
				timer.unref?.();
			}
			pending.set(id, {
				resolve: (r) => {
					if (timer) clearTimeout(timer);
					resolve(r);
				},
				reject: (e) => {
					if (timer) clearTimeout(timer);
					reject(e);
				},
			});
			try {
				proc.stdin!.write(`${JSON.stringify({ jsonrpc: "2.0", id, method, params })}\n`);
			} catch (err) {
				pending.delete(id);
				if (timer) clearTimeout(timer);
				reject(launchError ?? (err instanceof Error ? err : new Error(String(err))));
			}
		});
	}

	function notify(method: string, params?: unknown): void {
		try {
			proc.stdin!.write(`${JSON.stringify({ jsonrpc: "2.0", method, params })}\n`);
		} catch {
			// The server is gone; the pending rpc rejections already carry the reason.
		}
	}

	return {
		rpc,
		notify,
		terminate: () => {
			rl.close();
			proc.kill();
		},
	};
}

/** Remote servers use `{ type: "http" | "sse", url }`; stdio servers use `command`. */
function isRemoteServer(config: McpServerConfig): boolean {
	return config.type === "http" || config.type === "sse" || (!config.command && typeof config.url === "string");
}

function openMcpConnection(config: McpServerConfig): McpConnection {
	if (isRemoteServer(config)) {
		if (typeof config.url !== "string") {
			throw new Error(`MCP server "${config.name}" has type "${config.type}" but no "url"`);
		}
		return connectHttpMcpServer(
			{
				name: config.name,
				url: config.url,
				headers: config.headers,
				type: config.type === "sse" ? "sse" : "http",
			},
			mcpRemoteOptions.get(config.name),
		);
	}
	if (typeof config.command !== "string") {
		throw new Error(`MCP server "${config.name}" needs a "command" (stdio) or a "url" (http/sse)`);
	}
	return spawnMcpServer(config as McpServerConfig & { command: string });
}

async function connectMcpServer(config: McpServerConfig): Promise<{ conn: McpConnection; tools: McpToolDef[] }> {
	mcpConnections.get(config.name)?.terminate();

	const conn = openMcpConnection(config);
	mcpConnections.set(config.name, conn);

	try {
		await conn.rpc(
			"initialize",
			{
				protocolVersion: "2024-11-05",
				capabilities: { tools: {} },
				clientInfo: { name: "hoocode", version: "1.0.0" },
			},
			MCP_HANDSHAKE_TIMEOUT_MS,
		);

		// Per the MCP spec the client must acknowledge a successful initialize with the
		// initialized notification before issuing further requests; strict servers gate
		// tools/call on it.
		conn.notify("notifications/initialized");

		const toolsResult = (await conn.rpc("tools/list", {}, MCP_HANDSHAKE_TIMEOUT_MS)) as {
			tools?: McpToolDef[];
		};
		return { conn, tools: toolsResult.tools ?? [] };
	} catch (error) {
		// Don't leave a half-connected entry behind: remote transports have no
		// process-exit event to evict them, so getOrConnectMcp would keep handing
		// out the dead connection instead of reconnecting. Exception: a pending
		// browser authorization — terminating would kill the OAuth loopback
		// listener; the auth-completion handler reconnects that server itself.
		if (!mcpAuthPending.has(config.name)) {
			conn.terminate();
			if (mcpConnections.get(config.name) === conn) mcpConnections.delete(config.name);
		}
		throw error;
	}
}

/**
 * Return a live connection for a server, lazily reconnecting from the retained
 * config when the previous connection was torn down. Returns undefined only when
 * no config is known or a fresh connect attempt fails.
 */
async function getOrConnectMcp(name: string): Promise<McpConnection | undefined> {
	const existing = mcpConnections.get(name);
	if (existing) return existing;
	const config = mcpServerConfigs.get(name);
	if (!config) return undefined;
	try {
		const { conn } = await connectMcpServer(config);
		return conn;
	} catch {
		return undefined;
	}
}

let mcpExitCleanupInstalled = false;
/** Kill spawned MCP servers when the host process exits so they don't linger as
 *  orphans (their stdin merely goes idle, which doesn't terminate them). */
function installMcpExitCleanup(): void {
	if (mcpExitCleanupInstalled) return;
	mcpExitCleanupInstalled = true;
	process.once("exit", () => {
		for (const conn of mcpConnections.values()) {
			try {
				conn.terminate();
			} catch {
				// best-effort cleanup
			}
		}
		mcpConnections.clear();
	});
}

function buildMcpSchema(tool: McpToolDef): ReturnType<typeof Type.Object> {
	const props = tool.inputSchema?.properties ?? {};
	const required = new Set(tool.inputSchema?.required ?? []);
	const shape: Record<string, ReturnType<typeof Type.String>> = {};

	for (const [key, prop] of Object.entries(props)) {
		let field: ReturnType<typeof Type.String>;
		switch (prop.type) {
			case "number":
			case "integer":
				field = Type.Number({ description: prop.description }) as unknown as ReturnType<typeof Type.String>;
				break;
			case "boolean":
				field = Type.Boolean({ description: prop.description }) as unknown as ReturnType<typeof Type.String>;
				break;
			default:
				field = Type.String({ description: prop.description });
		}
		shape[key] = required.has(key) ? field : (Type.Optional(field) as unknown as ReturnType<typeof Type.String>);
	}

	return Type.Object(shape);
}

/**
 * Parse standard MCP config format (used by Claude Desktop, VS Code, etc.)
 * into hoocode's McpServerConfig format.
 */
function parseStandardMcpConfig(config: StandardMcpConfig, _source: string): McpServerConfig[] {
	if (!config.mcpServers) return [];

	const servers: McpServerConfig[] = [];
	for (const [name, serverConfig] of Object.entries(config.mcpServers)) {
		servers.push({
			name,
			command: serverConfig.command,
			args: serverConfig.args,
			env: serverConfig.env,
			type: serverConfig.type,
			url: serverConfig.url,
			headers: serverConfig.headers,
			background: serverConfig.background,
		});
	}
	return servers;
}

/**
 * Load MCP servers from a standard mcp.json file.
 * Returns an array of McpServerConfig, or empty array if file doesn't exist or is invalid.
 */
function loadStandardMcpFile(filePath: string): McpServerConfig[] {
	if (!existsSync(filePath)) return [];

	try {
		const content = readFileSync(filePath, "utf8");
		const config = JSON.parse(content) as StandardMcpConfig;
		return parseStandardMcpConfig(config, filePath);
	} catch {
		return [];
	}
}

/**
 * Build the full {@link ToolDefinition} for one MCP tool — the complete JSON
 * schema plus the connect/execute machinery. Shared by the eager path (register
 * every tool up front) and the deferred path (materialize on resolve), so both
 * produce identical, callable tools.
 */
function buildMcpToolDefinition(serverConfig: McpServerConfig, tool: McpToolDef): ToolDefinition {
	const toolName = `mcp_${serverConfig.name}_${tool.name}`;
	const schema = buildMcpSchema(tool);
	const capturedServer = serverConfig.name;
	const capturedTool = tool.name;
	// MCP tools default to background mode since they are external processes with potential high latency
	const isBackground = serverConfig.background !== false;

	return {
		name: toolName,
		label: `[MCP] ${serverConfig.name} › ${tool.name}`,
		description: tool.description,
		parameters: schema,
		background: isBackground,
		// Render a clean, prefixed title in chat — `MCP [server › tool] <args>` —
		// parallel to the subagent `Task [type] <desc>` line. Without this the
		// ToolExecutionComponent falls back to the raw `mcp_<server>_<tool>` name.
		// The args summary reuses the same helper as the background start/finish
		// messages so the chat title stays in sync with them.
		renderCall(args, theme) {
			const summary = summarizeArgs((args ?? {}) as Record<string, unknown>);
			const text =
				theme.fg("toolTitle", theme.bold("MCP ")) +
				theme.fg("mcp", `[${capturedServer} › ${capturedTool}]`) +
				(summary ? theme.fg("dim", ` ${summary}`) : "");
			return new Text(text, 0, 0);
		},
		async execute(
			_toolCallId: string,
			params: Static<typeof schema>,
			signal: AbortSignal,
			_onUpdate: AgentToolUpdateCallback,
		): Promise<AgentToolResult<McpToolDetails>> {
			const startTime = Date.now();
			// Background MCP tools get a task store entry so they appear in the task pane.
			// Foreground tools skip this (their result is awaited inline). The server
			// name rides in subagentMode and becomes the row's `[server]` origin tag;
			// the title carries just the tool.
			const task = isBackground
				? taskStore.create(capturedTool, { source: "mcp", subagentMode: capturedServer })
				: undefined;
			if (task) taskStore.update(task.id, { status: "in_progress" });

			// Lazily (re)connect: a dropped connection (server exit, process churn
			// between turns, a racing teardown) should transparently reconnect from
			// the retained config rather than permanently fail with "not connected".
			const activeConn = await getOrConnectMcp(capturedServer);
			if (!activeConn) {
				if (task) taskStore.update(task.id, { status: "failed" });
				return {
					content: [
						{ type: "text", text: `MCP server "${capturedServer}" is not connected (reconnect attempt failed)` },
					],
					details: { elapsed: Date.now() - startTime } as McpToolDetails,
				};
			}

			try {
				const abortPromise = new Promise<never>((_, reject) => {
					signal.addEventListener("abort", () => reject(new Error("Aborted")));
				});

				const result = await Promise.race([
					activeConn.rpc("tools/call", { name: capturedTool, arguments: params }),
					abortPromise,
				]);

				if (task) taskStore.update(task.id, { status: "done" });
				return {
					content: [{ type: "text", text: JSON.stringify(result, null, 2) }],
					details: { elapsed: Date.now() - startTime } as McpToolDetails,
				};
			} catch (error) {
				if (task) taskStore.update(task.id, { status: "failed" });
				throw error;
			}
		},
		renderResult(result, _options, theme, context) {
			const text = result.content
				.map((c) => (c.type === "text" ? c.text : ""))
				.filter(Boolean)
				.join("\n");
			if (!text) return new Text("", 0, 0);

			const details = result.details as McpToolDetails | undefined;
			const elapsed = details?.elapsed;
			const elapsedText = elapsed ? ` ${formatDurationSecs(elapsed / 1000)}` : "";
			const identity = `${capturedServer} › ${capturedTool}`;

			// Use context.isError to determine status (framework sets this when tools throw)
			const isError = context.isError;
			const statusGlyph = isError ? "✗" : "✓";
			const statusLabel = isError ? "failed" : "done";
			const statusColor = isError ? "error" : "success";

			const spine = (s: string) => theme.fg("borderMuted", s);
			const header =
				`${spine("╭")} ${theme.fg(statusColor, statusGlyph)} ` +
				`${theme.bold(theme.fg(statusColor, statusLabel))} ` +
				`${theme.fg("mcp", identity)}${elapsedText}`;
			const body = text
				.split("\n")
				.map((line) => `${spine("│")} ${theme.fg("toolOutput", line)}`)
				.join("\n");
			return new Text(`${header}\n${body}\n${spine("╰")}`, 0, 0);
		},
	} as ToolDefinition;
}

const RESOLVE_MCP_TOOLS_NAME = "ResolveMcpTools";

export function setupMcpLoader(hoo: ExtensionAPI): void {
	hoo.on("session_start", async (_event: SessionStartEvent, ctx: ExtensionContext) => {
		// A spawned subagent whose tool allowlist has no MCP tools is told by its
		// parent to skip server connection entirely (see SUBAGENT_SKIP_MCP_ENV).
		// Each connect is a ~15s-timeout handshake; doing it for a subagent that can
		// never call the tools is pure startup latency.
		if (subagentSkipMcp()) return;

		installMcpExitCleanup();
		// Rebuild the status registry from scratch: a reload or session switch runs
		// this pass again and the startup summary must not double-count.
		clearMcpServerStatuses();
		const allServerConfigs: McpServerConfig[] = [];
		const seenNames = new Set<string>();

		// 1. Load from standard mcp.json locations
		// User-level: ~/.agents/mcp.json
		const userAgentsConfig = loadStandardMcpFile(join(homedir(), ".agents", "mcp.json"));
		for (const config of userAgentsConfig) {
			if (!seenNames.has(config.name)) {
				seenNames.add(config.name);
				allServerConfigs.push(config);
			}
		}

		// Project-level: ./.agents/mcp.json
		const projectAgentsConfig = loadStandardMcpFile(join(ctx.cwd, ".agents", "mcp.json"));
		for (const config of projectAgentsConfig) {
			if (!seenNames.has(config.name)) {
				seenNames.add(config.name);
				allServerConfigs.push(config);
			}
		}

		// Claude Desktop: ~/.config/claude/mcp.json
		const claudeDesktopConfig = loadStandardMcpFile(join(homedir(), ".config", "claude", "mcp.json"));
		for (const config of claudeDesktopConfig) {
			if (!seenNames.has(config.name)) {
				seenNames.add(config.name);
				allServerConfigs.push(config);
			}
		}

		// 2. Load from hoocode's per-server format (existing behavior)
		const searchDirs = [join(HOOCODE_DIR, "mcp-servers"), join(ctx.cwd, ".hoocode", "mcp-servers")];

		for (const dir of searchDirs) {
			if (!existsSync(dir)) continue;

			let files: string[];
			try {
				files = (await readdir(dir)).filter((f) => f.endsWith(".json"));
			} catch {
				continue;
			}

			for (const file of files) {
				const cfgPath = join(dir, file);
				let serverConfig: McpServerConfig;

				try {
					serverConfig = JSON.parse(readFileSync(cfgPath, "utf8")) as McpServerConfig;
					if (!serverConfig.name || (!serverConfig.command && !serverConfig.url)) {
						ctx.ui.notify(`MCP: config "${file}" is missing required "name" or "command"/"url"`, "warning");
						continue;
					}
				} catch (err) {
					ctx.ui.notify(`MCP: failed to parse "${file}": ${String(err)}`, "error");
					continue;
				}

				// Skip if already loaded from standard config
				if (seenNames.has(serverConfig.name)) continue;
				seenNames.add(serverConfig.name);
				allServerConfigs.push(serverConfig);
			}
		}

		// 2b. Load from plugins/extensions that registered MCP servers during load.
		for (const entry of getExtensionMcpServers()) {
			for (const serverConfig of parseStandardMcpConfig(
				{ mcpServers: entry.mcpServers },
				`plugin:${entry.source}`,
			)) {
				if (seenNames.has(serverConfig.name)) continue;
				seenNames.add(serverConfig.name);
				allServerConfigs.push(serverConfig);
			}
		}

		// Deferral (spec §2): inject MCP tool names only and materialize each schema
		// on demand via ResolveMcpTools. Default-on and top-level only — a subagent that
		// needs MCP has this env cleared, so it eager-registers its allowlisted tools
		// at dispatch (the dispatch ↔ schema interaction) and they are immediately callable.
		const defer = deferMcpSchemas();
		const deferredCatalog: DeferredMcpToolEntry[] = [];
		// Retain each deferred tool's raw definition + config so ResolveMcpTools can
		// build the full ToolDefinition on request.
		const deferredByName = new Map<string, { serverConfig: McpServerConfig; tool: McpToolDef }>();
		const resolvedNames = new Set<string>();

		// Remote servers get OAuth support: tokens persist under
		// ~/.hoocode/mcp-auth, and when a server demands interactive (browser)
		// authorization the handshake fails fast while the flow keeps running in
		// the background — on completion the server is reconnected and its tools
		// registered (eagerly, since the deferred catalog is already sealed).
		const buildRemoteOptions = (serverConfig: McpServerConfig): McpRemoteOptions => ({
			authStorageDir: join(HOOCODE_DIR, "mcp-auth"),
			onAuthRequired: (authorizationUrl, completed) => {
				mcpAuthPending.add(serverConfig.name);
				ctx.ui.notify(
					`MCP: "${serverConfig.name}" requires authorization — complete the sign-in opened in your browser` +
						(authorizationUrl ? `: ${authorizationUrl}` : ""),
					"warning",
				);
				completed
					.then(async () => {
						mcpAuthPending.delete(serverConfig.name);
						try {
							const { tools } = await connectMcpServer(serverConfig);
							for (const tool of tools) hoo.registerTool(buildMcpToolDefinition(serverConfig, tool));
							setMcpServerStatus({
								name: serverConfig.name,
								toolCount: tools.length,
								background: serverConfig.background !== false,
								deferred: false,
								state: "connected",
							});
							ctx.ui.notify(
								`MCP: connected "${serverConfig.name}" after authorization (${tools.length} tool${tools.length === 1 ? "" : "s"})`,
								"info",
							);
						} catch (err) {
							ctx.ui.notify(
								`MCP: failed to connect "${serverConfig.name}" after authorization: ${String(err)}`,
								"error",
							);
						}
					})
					.catch((err: unknown) => {
						mcpAuthPending.delete(serverConfig.name);
						ctx.ui.notify(`MCP: authorization for "${serverConfig.name}" failed: ${String(err)}`, "error");
					});
			},
		});

		// 3. Connect to all servers and register (or defer) tools
		for (const serverConfig of allServerConfigs) {
			// Retain the config so a tool call can lazily reconnect a dropped server.
			mcpServerConfigs.set(serverConfig.name, serverConfig);
			mcpRemoteOptions.set(serverConfig.name, buildRemoteOptions(serverConfig));
			try {
				const { tools } = await connectMcpServer(serverConfig);

				for (const tool of tools) {
					const toolName = `mcp_${serverConfig.name}_${tool.name}`;
					if (defer) {
						deferredCatalog.push({ toolName, server: serverConfig.name, description: tool.description });
						deferredByName.set(toolName, { serverConfig, tool });
					} else {
						hoo.registerTool(buildMcpToolDefinition(serverConfig, tool));
					}
				}

				// A successful connect is reported by the startup resource summary (one
				// `mcp` cell plus a details row), not as a transient line above it.
				setMcpServerStatus({
					name: serverConfig.name,
					toolCount: tools.length,
					background: serverConfig.background !== false,
					deferred: defer,
					state: "connected",
				});
			} catch (err) {
				if (mcpAuthPending.has(serverConfig.name)) {
					setMcpServerStatus({
						name: serverConfig.name,
						toolCount: 0,
						background: serverConfig.background !== false,
						deferred: defer,
						state: "authorizing",
					});
					ctx.ui.notify(
						`MCP: "${serverConfig.name}" is waiting for browser authorization; tools will register once it completes`,
						"info",
					);
				} else {
					ctx.ui.notify(`MCP: failed to connect "${serverConfig.name}": ${String(err)}`, "error");
				}
			}
		}

		// 4. In deferred mode, register the single resolver that materializes schemas on demand.
		if (defer && deferredCatalog.length > 0) {
			// Feed the capability index. This is what lets the catalog above be
			// summarized instead of dumped: a tool the model cannot see in the
			// description is still reachable by describing what it needs.
			registerCapabilities("mcp-tool", toCapabilityDocs(deferredCatalog, true));
			resetCapabilitySearch();
			// Fire-and-forget: the lexical leg answers immediately, and the dense one
			// joins when (and if) it is ready. Awaiting here would put an embedding
			// model's startup in front of the session's.
			//
			// Indexed over everything registered, not just these tools: the dense
			// store is shared and keyed on the hash of whatever it was built from, so
			// passing a subset here would evict the other producers' vectors (and
			// theirs would evict these). Every caller passing the full set makes the
			// last write the complete one.
			void ensureDenseIndex(getCapabilities()).catch(() => {});

			const resolveParams = Type.Object(
				{
					names: Type.Optional(
						Type.Array(Type.String(), {
							description: "Exact MCP tool names to make callable (e.g. 'mcp_github_create_pr' or 'create_pr').",
						}),
					),
					query: Type.Optional(
						Type.String({
							description:
								"Describe the capability you need ('open a pull request', 'send a message') to find tools by " +
								"meaning when you do not know their names. Matching tools are resolved and become callable.",
						}),
					),
					limit: Type.Optional(Type.Number({ description: "Maximum tools to resolve from a query. Default 5." })),
				},
				{ additionalProperties: false },
			);
			hoo.registerTool({
				name: RESOLVE_MCP_TOOLS_NAME,
				label: RESOLVE_MCP_TOOLS_NAME,
				description:
					"MCP tools are connected but their schemas are loaded on demand to keep context small. Name the tool(s) " +
					"you need in `names`, or describe the capability in `query` to find them, then call the tool(s). " +
					"Available MCP tools:\n" +
					formatDeferredCatalog(deferredCatalog),
				promptSnippet: "Resolve deferred MCP tool schemas by name, or find them by describing the capability.",
				parameters: resolveParams,
				async execute(_toolCallId: string, params: Static<typeof resolveParams>) {
					const names = params.names ?? [];
					const query = params.query?.trim();
					const matched = selectResolvable(deferredCatalog, names);
					const seen = new Set(matched.map((m) => m.toolName));

					// Both parameters may be given: name what you know, describe the rest.
					let searchNote = "";
					if (query) {
						const { hits, legs } = await searchCapabilities(query, {
							kinds: ["mcp-tool"],
							limit: params.limit ?? 5,
						});
						for (const hit of hits) {
							const entry = deferredCatalog.find((e) => e.toolName === hit.doc.id);
							if (entry && !seen.has(entry.toolName)) {
								matched.push(entry);
								seen.add(entry.toolName);
							}
						}
						// Say which legs answered: a lexical-only result set on a
						// conceptual query is a weaker answer, and the caller should be
						// able to tell that from the result rather than guess.
						searchNote = hits.length
							? `\nMatched by ${legs.join("+") || "none"} search for "${query}".`
							: `\nNothing matched "${query}".`;
					}

					const newlyResolved: string[] = [];
					for (const entry of matched) {
						if (resolvedNames.has(entry.toolName)) continue;
						const raw = deferredByName.get(entry.toolName);
						if (!raw) continue;
						hoo.registerTool(buildMcpToolDefinition(raw.serverConfig, raw.tool));
						resolvedNames.add(entry.toolName);
						newlyResolved.push(entry.toolName);
					}
					const text = matched.length
						? `Resolved ${newlyResolved.length} MCP tool(s): ${matched.map((m) => m.toolName).join(", ")}. They are now callable.${searchNote}`
						: `No MCP tools matched: ${[...names, query].filter(Boolean).join(", ") || "(none)"}.${searchNote}`;
					return { content: [{ type: "text" as const, text }], details: undefined };
				},
			} as ToolDefinition);
		}
	});
}
